import { Close as CloseIcon, CloseRounded, Search as SearchIcon } from '@mui/icons-material';
import {
  Accordion,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField,
  ThemeProvider,
  Typography,
  useTheme,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { getAllCompaniesByType } from 'app/entities/company/company.reducer';
import { ICompany } from 'app/shared/model/company.model';
import { CompanyType } from 'app/shared/model/enumerations/company-type.model';
import { Loading } from 'app/shared/util/loading';
import debounce from 'lodash.debounce';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { translate } from 'react-jhipster';

interface IRepresentativeCompanyModalProps {
  handleSave: (data: { representativeCompanies: any[] }) => void;
  toggleModal: () => void;
  representativeCompanies: number[];
  companyEntity: ICompany;
}

export const RepresentativeCompanyModal = ({ handleSave, toggleModal, representativeCompanies }: IRepresentativeCompanyModalProps) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const companiesList = useAppSelector(state => state.company.entities);
  const loading = useAppSelector(state => state.company.loading);

  const [selectedCompanies, setSelectedCompanies] = useState(new Set(representativeCompanies.map(representative => representative)));
  const [debouncedCompanyFilterValue, setDebouncedCompanyFilterValue] = useState('');
  const [companyFilterValue, setCompanyFilterValue] = useState('');
  const [filtering, setFiltering] = useState(false);

  useEffect(() => {
    dispatch(getAllCompaniesByType({ type: CompanyType.REPRESENTATIVE }));
  }, []);

  const handleSelectAllCompanies = () => {
    if (selectedCompanies.size === companiesList.length) {
      setSelectedCompanies(new Set());
    } else {
      const newSelectedCompanies = new Set(companiesList.map(company => company.id));
      setSelectedCompanies(newSelectedCompanies as Set<number>);
    }
  };

  const handleSelectCompany = companyName => {
    const company = companiesList.find(c => c.name === companyName);
    if (!company) return;

    const isSelected = selectedCompanies.has(company.id);

    if (isSelected) {
      setSelectedCompanies(prevSelected => {
        const newSelected = new Set(prevSelected);
        newSelected.delete(company.id);
        return newSelected;
      });
    } else {
      setSelectedCompanies(prevSelected => {
        const newSelected = new Set(prevSelected);
        newSelected.add(company.id);
        return newSelected;
      });
    }
  };

  const handleCompanyInputChange = event => {
    setCompanyFilterValue(event.target.value);
    setFiltering(true);
    debouncedSetCompanyFilterValue(event.target.value);
  };

  const debouncedSetCompanyFilterValue = useCallback(
    debounce(value => {
      setDebouncedCompanyFilterValue(value);
      setFiltering(false);
    }, 1000),
    []
  );

  const prepareDataForSave = () => {
    return {
      representativeCompanies: Array.from(selectedCompanies.values()),
    };
  };

  const handleSaveClick = () => {
    const data = prepareDataForSave();
    handleSave(data);
  };

  const filteredCompanies = useMemo(() => {
    const companiesFiltered = companiesList.filter(company => company.name.toLowerCase().includes(debouncedCompanyFilterValue.toLowerCase()));

    return companiesFiltered.sort((a, b) => {
      const isRepresentedA = representativeCompanies.includes(a.id);
      const isRepresentedB = representativeCompanies.includes(b.id);

      if (isRepresentedA && !isRepresentedB) return -1;
      if (!isRepresentedA && isRepresentedB) return 1;

      return a.name.localeCompare(b.name);
    });
  }, [companiesList, debouncedCompanyFilterValue, representativeCompanies]);

  return (
    <ThemeProvider theme={theme}>
      <Dialog
        open={true}
        onClose={toggleModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '15px', background: theme.palette.primary.main } }}
        sx={{
          boxShadow:
            'rgba(0, 0, 0, 0.25) 0px 54px 55px, rgba(0, 0, 0, 0.12) 0px -12px 30px, rgba(0, 0, 0, 0.12) 0px 4px 6px, rgba(0, 0, 0, 0.17) 0px 12px 13px, rgba(0, 0, 0, 0.09) 0px -3px 5px',
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Selecione os representantes</Typography>
          <IconButton onClick={toggleModal}>
            <CloseRounded color="secondary" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            size="small"
            value={companyFilterValue}
            onChange={handleCompanyInputChange}
            placeholder="Buscar por representante"
            InputProps={{
              sx: { borderRadius: '8px', p: '0 8px 2px 0' },
              endAdornment: (
                <InputAdornment position="end">
                  {!companyFilterValue ? (
                    <SearchIcon color="action" />
                  ) : (
                    <IconButton size="small" onClick={() => handleCompanyInputChange({ target: { value: '' } } as React.ChangeEvent<HTMLInputElement>)}>
                      <CloseIcon />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }}
          />
          {!loading && !!companiesList?.length && (
            <Box mt={2}>
              <FormControlLabel
                control={<Checkbox checked={selectedCompanies.size === companiesList.length} onChange={handleSelectAllCompanies} color="primary" />}
                label="Selecionar todos"
              />
            </Box>
          )}
          <Box mb={2}>
            {loading || filtering ? (
              <Loading />
            ) : filteredCompanies?.length ? (
              filteredCompanies.map((company, index) => (
                <React.Fragment key={index}>
                  <Accordion
                    sx={{
                      my: 1,
                      borderRadius: '8px',
                      '&:before': {
                        display: 'none',
                      },
                    }}
                    expanded={false}
                  >
                    <AccordionSummary>
                      <FormControlLabel
                        control={<Checkbox checked={selectedCompanies.has(company.id)} onChange={() => handleSelectCompany(company.name)} color="primary" />}
                        label={company.name}
                      />
                    </AccordionSummary>
                  </Accordion>
                </React.Fragment>
              ))
            ) : (
              <Typography mt={3} textAlign="center">
                Nenhum representante encontrado.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: '8px 24px', my: 2 }}>
          <Button onClick={toggleModal} disableRipple variant="text" sx={{ color: 'text.primary' }}>
            {translate('fleetSenseApp.truckDeviceStatus.frontEnd.buttonNames.cancel')}
          </Button>
          <Button onClick={handleSaveClick} disableRipple variant="contained" color="secondary">
            {translate('fleetSenseApp.truckDeviceStatus.frontEnd.buttonNames.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default RepresentativeCompanyModal;
